/**
 * Новости в приложении: модель экрана поверх витринного DTO
 * (`@dorixona/api/news`). Тексты держим на обоих языках — язык выбирает экран
 * при отрисовке, так смена RU/UZ не требует нового запроса.
 */
import { mediaUrl } from '@dorixona/api'
import type { NewsPage, PublicNewsDto, PublicNewsItemDto } from '@dorixona/api/news'
import type { Language } from '@dorixona/i18n'
import { parseMs } from '@dorixona/utils'

export type Texts = Record<Language, string>

/** Новость в ленте и на полке главной — без текста. */
export interface NewsItem {
  id: number
  title: Texts
  /** Анонс — простой текст. */
  lead: Texts
  photo: string
  /** Дата публикации, мс; 0 — сервер её не прислал. */
  publishedAt: number
}

/** Новость на её экране: с текстом (HTML редактора админки). */
export interface News extends NewsItem {
  body: Texts
}

/** Порция ленты и общий итог сервера. */
export interface NewsListPage {
  items: NewsItem[]
  total: number
}

/** Текст на выбранном языке и язык, на котором он на самом деле написан. */
export interface Resolved {
  text: string
  lang: Language
}

/**
 * Текст на языке интерфейса. Узбекского нет — показываем русский: русский
 * админка заполняет обязательно. `lang` говорит, на каком языке вышло, — по
 * нему читалка экрана выбирает произношение.
 */
export function resolve(texts: Texts, language: Language): Resolved {
  const own = texts[language].trim()
  if (own) return { text: own, lang: language }
  return { text: texts.ru.trim(), lang: 'ru' }
}

/** Только текст — там, где язык для читалки не важен. */
export function pick(texts: Texts, language: Language): string {
  return resolve(texts, language).text
}

export function toNewsItem(dto: PublicNewsItemDto, apiBaseUrl: string): NewsItem {
  return {
    id: dto.id,
    title: { ru: dto.title_ru, uz: dto.title_uz },
    lead: { ru: dto.lead_ru, uz: dto.lead_uz },
    photo: mediaUrl(dto.photo_url, apiBaseUrl),
    publishedAt: parseMs(dto.published_at),
  }
}

export function toNews(dto: PublicNewsDto, apiBaseUrl: string): News {
  return { ...toNewsItem(dto, apiBaseUrl), body: { ru: dto.body_ru, uz: dto.body_uz } }
}

export function toNewsListPage(
  page: NewsPage<PublicNewsItemDto>,
  apiBaseUrl: string,
): NewsListPage {
  return { items: page.items.map((dto) => toNewsItem(dto, apiBaseUrl)), total: page.total }
}

/**
 * Номер следующей порции ленты. Пустая порция — конец, даже если итог обещал
 * больше: новость могли снять с публикации, пока покупатель листал.
 */
export function nextPage(last: NewsListPage, pages: NewsListPage[]): number | undefined {
  const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
  return last.items.length > 0 && loaded < last.total ? pages.length + 1 : undefined
}

/**
 * Путь приложения из ссылки в тексте новости: начинается с одного `/`.
 * `//host` и `/\host` браузер читает как чужой сайт без схемы — это не наш путь.
 */
export function internalPath(href: string): string | null {
  return /^\/(?![/\\])/.test(href) ? href : null
}
